import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from '~/src/lib/utils'

export const buttonVariants = cva(
  'relative inline-flex items-center justify-center whitespace-nowrap text-center font-semibold transition-all border border-black/20 dark:border-white/20',
  {
    variants: {
      variant: {
        primary: 'bg-black text-white',
        secondary: 'bg-white text-black',
        action: 'bg-orange text-white',
        danger: 'bg-red-500 text-white',
        muted: 'bg-[#00A3FF] text-white',
        dull: 'bg-[#EBEBEB] text-black',
        outline: 'bg-transparent',
        custom: '!border-none', // Assuming custom means no default border
      },
      size: {
        xs: 'h-[30px] rounded-md px-1 text-xs',
        sm: 'h-[35px] rounded-md px-2 text-sm md:px-3',
        md: 'h-[40px] rounded-md px-2 text-base md:px-3',
        lg: 'h-12 rounded-xl px-5 text-lg md:h-14 md:px-6',
        xl: 'h-[60px] rounded-xl px-6 text-xl md:px-8',
        '2xl': 'h-[70px] rounded-xl px-6 text-xl md:h-[90px] md:px-8',
      },
      rounded: {
        true: '!rounded-full',
      },
      fullWidth: {
        true: 'w-full',
      },
      hideBorder: {
        true: '!border-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  }
)

interface BaseProps<T extends React.ElementType = 'button'>
  extends VariantProps<typeof buttonVariants> {
  as?: T
  processing?: boolean
}

type ButtonProps<T extends React.ElementType> = BaseProps<T> &
  Omit<React.ComponentPropsWithRef<T>, keyof BaseProps<T>>

function Button<T extends React.ElementType = 'button'>(props: ButtonProps<T>) {
  const {
    className,
    disabled,
    processing,
    rounded,
    hideBorder,
    fullWidth,
    variant,
    type = 'button',
    size,
    children,
    as: Component = 'button',
    ref,
    ...rest
  } = props

  return (
    <Component
      ref={ref}
      type={Component === 'button' ? type : undefined}
      disabled={disabled || processing}
      className={cn(
        buttonVariants({ variant, size, rounded, fullWidth, hideBorder }),
        {
          'opacity-50': (disabled || processing) && variant !== 'custom',
          'hover:border-black/80 dark:hover:border-white/80':
            !processing && !disabled && variant !== 'custom',
        },
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  )
}

Button.displayName = 'Button'

export default Button
